import { useMemo } from 'react'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  LabelList
} from 'recharts'

const formatCLP = n => Number(n ?? 0).toLocaleString('es-CL')
const RE_YM = /^\d{4}-\d{2}$/
const RE_YMD = /^\d{4}-\d{2}-\d{2}/
const RE_YM_COMPACT = /^\d{6}$/
const RE_MY = /^\d{2}-\d{4}$/
const RE_YM_SLASH = /^\d{4}\/\d{2}$/
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
const MONTHS_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const isYYYYMM = s => typeof s === 'string' && RE_YM.test(s)
const COLOR_MUTED = 'rgb(var(--muted))'
const COLOR_GRID = 'rgb(var(--border))'

const pickPeriodValue = (value) => {
  if (!value) return ''
  if (typeof value === 'object') {
    return (
      value.name ??
      value.mes ??
      value.month ??
      value.period ??
      value.monthKey ??
      value.fecha ??
      value.date ??
      value.label ??
      value.labelShort ??
      value.key ??
      ''
    )
  }
  return value
}

const normalizePeriodKey = (value) => {
  const raw = pickPeriodValue(value)
  if (!raw) return ''
  if (raw instanceof Date) return raw.toISOString().slice(0, 7)
  const s = String(raw)
  if (RE_YM.test(s)) return s
  if (RE_YMD.test(s)) return s.slice(0, 7)
  if (RE_YM_COMPACT.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
  if (RE_YM_SLASH.test(s)) return s.replace('/', '-')
  if (RE_MY.test(s)) {
    const [m, y] = s.split('-')
    return `${y}-${m}`
  }
  return s
}

const getMonthLabel = (val, short = false, fallback = '') => {
  if (!val && fallback) return fallback
  const key = normalizePeriodKey(val)
  if (isYYYYMM(key)) {
    const monthIndex = parseInt(key.slice(5, 7), 10) - 1
    const list = short ? MONTHS_SHORT_ES : MONTHS_ES
    return list[monthIndex] ?? fallback ?? key
  }
  const raw = pickPeriodValue(val)
  if (typeof raw === 'string' && raw) return raw
  return fallback || key
}

// Formateo compacto para el eje/labels
const formatMillions = (n) => {
  const v = Number(n || 0)
  if (v === 0) return '0'
  return `${Math.round(v / 1_000_000)}M`
}

const CustomTooltip = ({ active, payload, incomeFill, expenseFill }) => {
  if (!active || !payload?.length) return null
  const raw = payload[0].payload
  const month = raw.periodo || raw.mes || raw.label // usa la key real de tu dataset
  const cobrar  = payload.find(p => p.dataKey === 'cobrar')?.value || 0
  const pagar   = payload.find(p => p.dataKey === 'pagar')?.value || 0
  const totalNeto = Number(raw.totalFacturadoNeto ?? (cobrar + pagar))

  return (
    <div className="bg-surface rounded-2xl shadow-card border border-border px-4 py-3 text-text">
      <div className="font-extrabold text-lg mb-1">{getMonthLabel(month)} </div>

      {/* Por cobrar -> ROJO */}
      <div className="flex items-center gap-2">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: expenseFill }} />
        <span className="font-semibold" style={{ color: expenseFill }}>Por cobrar:</span>
        <span className="ml-1 font-medium">{formatCLP(cobrar)}</span>
      </div>

      {/* Cobrado -> VERDE */}
      <div className="flex items-center gap-2 mt-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: incomeFill }} />
        <span className="font-semibold" style={{ color: incomeFill }}>Cobrado:</span>
        <span className="ml-1 font-medium">{formatCLP(pagar)}</span>
      </div>

      {/* Total facturado (neto) */}
      <div className="mt-2 pt-2 border-t border-border">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Total facturado:</span>
          <span className="ml-1 font-medium">{formatCLP(totalNeto)}</span>
        </div>
      </div>
    </div>
  )
}

// hook para leer colores de Tailwind en runtime
const useTWColor = (className, fallback) =>
  useMemo(() => {
    if (typeof window === 'undefined' || !document?.body) return fallback
    const el = document.createElement('span')
    el.style.display = 'none'
    el.className = className
    document.body.appendChild(el)
    const color = getComputedStyle(el).color || fallback
    document.body.removeChild(el)
    return color
  }, [className, fallback])

export default function ChartStackedAging({ data = [], onSelectMonth }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const incomeFill  = useTWColor('text-success', 'rgb(var(--success))') // Cobrado (verde)
  const expenseFill = useTWColor('text-danger', 'rgb(var(--danger))') // Por cobrar (rojo)

  // Etiqueta superior que muestra el Total facturado (neto) sólo en el tope de la pila
  const TopTotal = (props) => {
    const { x, y, width, payload, dataKey } = props
    const p = payload || {}
    const isTop =
      (dataKey === 'pagar' && Number(p.pagar) > 0) ||
      (dataKey === 'cobrar' && Number(p.pagar) <= 0 && Number(p.cobrar) > 0)

    if (!isTop) return null
    const totalNeto = Number(p.totalFacturadoNeto ?? (Number(p.cobrar || 0) + Number(p.pagar || 0)))
    if (totalNeto <= 0) return null

    const cx = (x ?? 0) + (Number(width) || 0) / 2
    const cy = (y ?? 0) - 6
    return (
      <text x={cx} y={cy} textAnchor="middle" fill={COLOR_MUTED} style={{ fontSize: 10, fontWeight: 600 }}>
        {formatMillions(totalNeto)}
      </text>
    )
  }

  const baseChartData = useMemo(() => {
    const src = Array.isArray(data) ? data : []
    return src.map(d => {
      const cobrar  = Number(d.cobrar  ?? d['Por cobrar'] ?? d.porCobrar ?? 0) || 0
      const pagar   = Number(d.pagar   ?? d['Por pagar']  ?? d.cobrado   ?? 0) || 0
      // Si viene precalculado desde el container, úsalo; si no, calcúlalo
      const totalFacturadoNeto = Number(d.totalFacturadoNeto ?? (cobrar + pagar)) || 0

      const rawLabel = d.label ?? d.labelShort ?? d.name ?? ''
      const rawPeriod = d.key ?? d.mes ?? d.month ?? d.period ?? d.monthKey ?? d.fecha ?? d.date ?? d.labelShort ?? d.label ?? rawLabel
      const periodKey = normalizePeriodKey(rawPeriod)
      const key  = periodKey || String(rawPeriod ?? '')
      const name = key
      const label = getMonthLabel(key, false, rawLabel || key)
      const labelShort = getMonthLabel(key, true, rawLabel || key)
      return {
        ...d,
        key,
        name,
        label,
        labelShort,
        totalFacturadoNeto: Math.max(totalFacturadoNeto, 0),
        cobrar:  Math.max(cobrar, 0),
        pagar:   Math.max(pagar, 0),
      }
    })
  }, [data])
  const chartData = baseChartData
  const mobileHeight = 260
  const desktopHeight = 320
  const chartHeight = isMobile ? mobileHeight : desktopHeight
  const showMobileLabels = isMobile && chartData.length > 0
  const showDesktopLabels = !isMobile && chartData.length > 0
  const renderMonthTick = (props) => {
    const { x, y, payload } = props || {}
    const label = getMonthLabel(payload?.value, true, payload?.value)
    if (!label) return null
    return (
        <text
          x={x}
          y={(y ?? 0) + 12}
          textAnchor="middle"
          fill={COLOR_MUTED}
          fontSize={isMobile ? 11 : 12}
        >
          {label}
        </text>
    )
  }

  // Dominio y ticks del eje Y (considera las 3 series apiladas)
  const { yMax, yTicks } = useMemo(() => {
    const STEP = 25_000_000
    const totals = (chartData ?? []).map(d =>
      (Number(d.cobrar) || 0) + (Number(d.pagar) || 0)
    )
    const maxVal = totals.length ? Math.max(...totals) : 0
    const top = Math.max(STEP, Math.ceil(maxVal / STEP) * STEP)
    const ticks = []
    for (let v = 0; v <= top; v += STEP) ticks.push(v)
    return { yMax: top, yTicks: ticks }
  }, [chartData])


  return (
    <div className="w-full">
      <div style={{ height: chartHeight }}>
        <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="horizontal"
          margin={isMobile ? { top: 12, right: 8, left: 8, bottom: 28 } : { top: 12, right: 20, left: 0, bottom: 20 }}
          barCategoryGap={isMobile ? '20%' : '0%'}
          barGap={0}
          maxBarSize={isMobile ? 26 : 44}
          cursor="pointer"
          onClick={(e) => {
            const idx = e?.activeTooltipIndex
            if (idx != null && onSelectMonth) {
              const key = chartData[idx]?.key ?? chartData[idx]?.name
              if (key) onSelectMonth(key)
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLOR_GRID} />

          <>
            <XAxis
              dataKey="name"
              ticks={chartData.map(item => item.name)}
              padding={{ left: 0, right: 0 }}
              interval={0}
              height={isMobile ? 22 : 8}
              tickMargin={0}
              tick={isMobile ? renderMonthTick : false}
              tickLine={false}
              axisLine={false}
              minTickGap={0}
              allowDuplicatedCategory={false}
            />
            <YAxis
              domain={[0, yMax]}
              ticks={yTicks}
              tickFormatter={formatMillions}
              allowDecimals={false}
              tickLine={false}
              tick={{ fontSize: isMobile ? 11 : 13, fill: COLOR_MUTED }}
            />
          </>

          <Tooltip
            cursor={{ fill: 'rgb(var(--border) / 0.35)' }}
            content={
              <CustomTooltip
                incomeFill={incomeFill}
                expenseFill={expenseFill}
              />
            }
          />

          {/* Barras apiladas: Rojo=Por cobrar, Verde=Cobrado */}
          <Bar dataKey="cobrar" stackId="stack" fill={expenseFill}>
            {chartData.map((d, i) => {
              const r = (Number(d.pagar) <= 0 && Number(d.cobrar) > 0)
                ? (isMobile ? [0, 8, 8, 0] : [8, 8, 0, 0])
                : [0, 0, 0, 0]
              return <Cell key={`cobrar-${i}`} radius={r} />
            })}
            {!isMobile && <LabelList dataKey="cobrar" content={(p) => <TopTotal {...p} />} />}
          </Bar>

          <Bar dataKey="pagar" stackId="stack" fill={incomeFill}>
            {chartData.map((d, i) => {
              const r = (Number(d.pagar) > 0)
                ? (isMobile ? [0, 8, 8, 0] : [8, 8, 0, 0])
                : [0, 0, 0, 0]
              return <Cell key={`pagar-${i}`} radius={r} />
            })}
            {!isMobile && <LabelList dataKey="pagar" content={(p) => <TopTotal {...p} />} />}
          </Bar>
        </BarChart>
        </ResponsiveContainer>
      </div>
      {showMobileLabels && (
        <div
          className="mt-2 grid text-xs text-muted"
          style={{ gridTemplateColumns: `repeat(${chartData.length}, minmax(0, 1fr))` }}
        >
          {chartData.map(item => (
            <div key={item.key} className="text-center">
              {item.labelShort || item.label || getMonthLabel(item.key, true, item.key)}
            </div>
          ))}
        </div>
      )}
      {showDesktopLabels && (
        <div
          className="mt-2 grid text-xs text-muted"
          style={{ gridTemplateColumns: `repeat(${chartData.length}, minmax(0, 1fr))` }}
        >
          {chartData.map(item => (
            <div key={item.key} className="text-center">
              {item.labelShort || item.label || getMonthLabel(item.key, true, item.key)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
